import React, { useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { DataMapping, DataMappingExcel, SmartExcelExtractor } from '../mapping';
import DataFormAttachment from './DataFormAttachment';
import { fileParser } from '../../utils/fileParser';

const DataExtraction = ({
  // Form configuration
  isSingleTableMode,
  isDualTableMode,
  isTableViewMode,
  tableName,
  tables,
  columns,
  tableConfig,
  parentConfig,
  childConfig,
  sectionConfig,
  
  // Data and handlers
  formData,
  parentData,
  matchKey,
  
  // Callbacks
  onDataExtracted,
  onAttachmentSaved,
  onDebugInfoChanged,
  
  // UI state
  droppedFile,
  onFileProcessed
}) => {
  // File processing states
  const [showAttachmentForm, setShowAttachmentForm] = useState(false);
  const [showDataMapping, setShowDataMapping] = useState(false);
  const [showEnhancedMapping, setShowEnhancedMapping] = useState(false);
  const [showExcelMapping, setShowExcelMapping] = useState(false);
  const [showSmartExcelExtractor, setShowSmartExcelExtractor] = useState(false);
  const [showOpenAIExtractor, setShowOpenAIExtractor] = useState(false);
  const [mappedData, setMappedData] = useState(null);
  const [enhancedParsedData, setEnhancedParsedData] = useState(null);
  const [showFileOptions, setShowFileOptions] = useState(false);
  
  // Debug: Track showFileOptions state changes
  useEffect(() => {
    console.log('🔥 DEBUG: showFileOptions state changed to:', showFileOptions);
  }, [showFileOptions]);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isProcessingOpenAI, setIsProcessingOpenAI] = useState(false);
  const [openAIResults, setOpenAIResults] = useState(null);
  const [excelSheets, setExcelSheets] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [currentWorkbook, setCurrentWorkbook] = useState(null);
  
  // OpenAI specific states
  const [openAISheetNames, setOpenAISheetNames] = useState([]);
  const [openAISelectedSheet, setOpenAISelectedSheet] = useState(null);
  const [openAISheetText, setOpenAISheetText] = useState('');
  const [showOpenAISheetSelector, setShowOpenAISheetSelector] = useState(false);
  const [showOpenAIDataPreview, setShowOpenAIDataPreview] = useState(false);
  const [openAIPreviewData, setOpenAIPreviewData] = useState(null);
  
  // Debug states for AI extraction review
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [extractionHistory, setExtractionHistory] = useState([]);
  
  // Image extraction states
  const [extractionStatus, setExtractionStatus] = useState('idle'); // 'idle', 'processing', 'compressing', 'error'
  const [extractionProgress, setExtractionProgress] = useState(0);

  // Excel sheet selection states (for all Excel extraction methods)
  const [showExcelSheetSelector, setShowExcelSheetSelector] = useState(false);
  
  // Debug: Track showExcelSheetSelector state changes
  useEffect(() => {
    console.log('🔥 DEBUG: showExcelSheetSelector state changed to:', showExcelSheetSelector);
  }, [showExcelSheetSelector]);
  const [excelSheetNames, setExcelSheetNames] = useState([]);
  const [selectedExcelSheet, setSelectedExcelSheet] = useState(null);
  const [pendingExcelAction, setPendingExcelAction] = useState(null); // 'mapping', 'smart', 'openai', 'readToForm'

  // PDF page selection states
  const [showPDFPageSelector, setShowPDFPageSelector] = useState(false);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [selectedPDFPage, setSelectedPDFPage] = useState(1);
  const [pdfFileUrl, setPdfFileUrl] = useState(null);
  const [pendingPDFAction, setPendingPDFAction] = useState(null); // 'ai', 'readToForm'

  // Helper function to set debug info and notify parent
  const updateDebugInfo = (debugData) => {
    setDebugInfo(debugData);
    setExtractionHistory(prev => [debugData, ...prev.slice(0, 9)]); // Keep last 10 extractions
    if (onDebugInfoChanged) {
      onDebugInfoChanged(debugData);
    }
  };

  // Helper function to load Excel workbook and get sheet names
  const loadExcelWorkbook = async (file) => {
    try {
      console.log('📊 Loading Excel workbook:', file.name, 'Size:', file.size);
      const arrayBuffer = await file.arrayBuffer();
      console.log('📊 ArrayBuffer loaded, size:', arrayBuffer.byteLength);
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      console.log('📊 Workbook loaded, sheets found:', workbook.SheetNames);
      return {
        workbook,
        sheetNames: workbook.SheetNames
      };
    } catch (error) {
      console.error('❌ Failed to read Excel file:', error);
      throw new Error('Failed to read Excel file: ' + error.message);
    }
  };

  // Helper function to handle Excel sheet selection
  const handleExcelSheetSelect = (sheetName) => {
    console.log('📋 Excel sheet selected:', sheetName, 'Pending action:', pendingExcelAction);
    setSelectedExcelSheet(sheetName);
    setShowExcelSheetSelector(false);
    
    // Execute the pending action with the selected sheet
    if (pendingExcelAction) {
      console.log('▶️ Executing pending action:', pendingExcelAction);
      switch (pendingExcelAction) {
        case 'mapping':
          console.log('📊 Opening Excel mapping modal');
          setShowExcelMapping(true);
          break;
        case 'smart':
          console.log('🚀 Opening Smart Excel extractor modal');
          setShowSmartExcelExtractor(true);
          break;
        case 'openai':
          console.log('🤖 Starting OpenAI Excel extraction with sheet');
          handleOpenAIExcelExtractionWithSheet(sheetName);
          break;
        case 'ai':
          console.log('🖼️ Starting AI Image extraction with sheet');
          handleAIImageExtractorWithSheet(sheetName);
          break;
        case 'readToForm':
          console.log('📝 Reading to form with sheet');
          handleReadToFormWithSheet(sheetName);
          break;
        default:
          console.warn('⚠️ Unknown pending action:', pendingExcelAction);
          break;
      }
      setPendingExcelAction(null);
    } else {
      console.warn('⚠️ No pending action found after sheet selection');
    }
  };

  // Helper function to handle PDF page selection
  const handlePDFPageSelect = (pageNumber) => {
    setSelectedPDFPage(pageNumber);
    setShowPDFPageSelector(false);
    
    // Execute the pending action with the selected page
    if (pendingPDFAction) {
      switch (pendingPDFAction) {
        case 'ai':
          handleAIImageExtractorWithPage(pageNumber);
          break;
        case 'readToForm':
          handleReadToFormWithPDFPage(pageNumber);
          break;
        default:
          break;
      }
      setPendingPDFAction(null);
    }
  };

  // Helper function to check if file needs sheet/page selection
  const needsSelection = (file) => {
    const fileName = file.name.toLowerCase();
    if (fileName.match(/\.(xlsx|xls)$/i)) {
      return 'excel';
    } else if (fileName.match(/\.(pdf)$/i)) {
      return 'pdf';
    }
    return null;
  };

  // Helper function to prepare file for extraction with selection
  const prepareFileForExtraction = async (file, action) => {
    console.log('🔄 Preparing file for extraction:', file.name, 'Action:', action);
    const fileType = needsSelection(file);
    console.log('📁 File type detected:', fileType);
    
    if (fileType === 'excel') {
      try {
        const { workbook, sheetNames } = await loadExcelWorkbook(file);
        setCurrentWorkbook(workbook);
        setExcelSheetNames(sheetNames);
        
        if (sheetNames.length > 1) {
          // Multiple sheets - show selector
          console.log('📋 Multiple sheets detected, showing selector for:', sheetNames);
          console.log('🔥 DEBUG: Setting pendingExcelAction to:', action);
          console.log('🔥 DEBUG: Current showExcelSheetSelector state:', showExcelSheetSelector);
          setPendingExcelAction(action);
          setShowExcelSheetSelector(true);
          console.log('🔥 DEBUG: Sheet selector should now be visible');
          console.log('🔥 DEBUG: Returning false to prevent immediate processing');
          return false; // Don't proceed yet
        } else {
          // Single sheet - proceed directly
          console.log('📋 Single sheet detected, proceeding with:', sheetNames[0]);
          setSelectedExcelSheet(sheetNames[0]);
          return true; // Proceed with extraction
        }
      } catch (error) {
        console.error('❌ Failed to prepare Excel file:', error);
        alert('Failed to read Excel file: ' + error.message);
        return false;
      }
    } else if (fileType === 'pdf') {
      try {
        // Create object URL for PDF preview
        const fileUrl = URL.createObjectURL(file);
        setPdfFileUrl(fileUrl);
        
        // Get PDF page count from backend
        const formData = new FormData();
        formData.append('pdf', file);
        
        const response = await fetch('/api/openai/get-pdf-info', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Failed to get PDF info: ${response.statusText}`);
        }
        
        const pdfInfo = await response.json();
        setPdfPageCount(pdfInfo.pageCount);
        setSelectedPDFPage(1);
        
        if (pdfInfo.pageCount > 1) {
          // Multiple pages - show selector
          setPendingPDFAction(action);
          setShowPDFPageSelector(true);
          return false; // Don't proceed yet
        } else {
          // Single page - proceed directly
          return true; // Proceed with extraction
        }
      } catch (error) {
        console.error('Failed to prepare PDF file:', error);
        alert('Failed to read PDF file: ' + error.message);
        return false;
      }
    }
    
    return true; // No selection needed
  };

  // OpenAI Excel analysis function
  const analyzeExcelWithOpenAI = async (excelData, formFieldDefinitions, mode) => {
    try {
      console.log('🤖 Starting OpenAI Excel analysis...');
      
      // Prepare field definitions for OpenAI
      const fieldDescriptions = formFieldDefinitions.map(field => ({
        name: field.name,
        displayName: field.displayName || field.label || field.name,
        type: field.type || 'text',
        format: field.format,
        description: field.description || field.desc || '',
        required: field.required || false,
        isArray: field.isArray || false,
        lookupTable: field.lookupTable || null,
        unit: field.unit || null
      }));

      // Prepare Excel data structure for analysis
      const excelStructure = {
        sheets: Object.keys(excelData).map(sheetName => ({
          name: sheetName,
          rowCount: excelData[sheetName].length,
          columnCount: excelData[sheetName][0]?.length || 0,
          headers: excelData[sheetName][0] || [],
          sampleRows: excelData[sheetName].slice(1, 6)
        }))
      };

      // Create OpenAI prompt
      const prompt = `
You are an expert data extraction assistant. I need you to analyze an Excel file and map its data to specific form fields.

**Excel File Structure:**
${JSON.stringify(excelStructure, null, 2)}

**Target Form Fields:**
${JSON.stringify(fieldDescriptions, null, 2)}

**Instructions:**
1. Analyze the Excel structure and identify where each form field's data can be found
2. For single-value fields, provide the exact cell location or range
3. For array fields (isArray: true), provide the range that contains multiple values
4. Consider field types (date, number, text, etc.) when matching data
5. Look for patterns in headers, data organization, and field naming
6. Handle different Excel layouts (vertical forms, horizontal tables, mixed layouts)

**Response Format (JSON only):**
{
  "mappings": [
    {
      "fieldName": "string",
      "fieldType": "string",
      "dataLocation": {
        "sheet": "string",
        "type": "cell|range|column|row",
        "coordinates": "string",
        "pattern": "string"
      },
      "confidence": 0.95,
      "notes": "string"
    }
  ],
  "overallConfidence": 0.9,
  "recommendations": [
    "string"
  ]
}

Please analyze the Excel data and provide the mapping instructions.`;

      // Make API call to OpenAI
      const response = await fetch('/api/openai/analyze-excel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          excelData: excelStructure,
          fieldDefinitions: fieldDescriptions
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('🤖 OpenAI analysis result:', result);
      
      return result;
    } catch (error) {
      console.error('❌ OpenAI Excel analysis failed:', error);
      throw error;
    }
  };

  // Function to extract data using OpenAI mapping results
  const extractDataUsingOpenAIMapping = (excelData, mappingResult) => {
    try {
      console.log('📊 Extracting data using OpenAI mapping...');
      
      const extractedData = {};
      
      mappingResult.mappings.forEach(mapping => {
        const { fieldName, dataLocation } = mapping;
        const sheet = excelData[dataLocation.sheet];
        
        if (!sheet) {
          console.warn(`Sheet "${dataLocation.sheet}" not found`);
          return;
        }
        
        let value = null;
        
        switch (dataLocation.type) {
          case 'cell':
            const cellCoords = parseCellCoordinate(dataLocation.coordinates);
            if (cellCoords && sheet[cellCoords.row] && sheet[cellCoords.row][cellCoords.col] !== undefined) {
              value = sheet[cellCoords.row][cellCoords.col];
            }
            break;
            
          case 'range':
            const rangeCoords = parseRangeCoordinate(dataLocation.coordinates);
            if (rangeCoords) {
              const values = [];
              for (let row = rangeCoords.startRow; row <= rangeCoords.endRow; row++) {
                for (let col = rangeCoords.startCol; col <= rangeCoords.endCol; col++) {
                  if (sheet[row] && sheet[row][col] !== undefined && sheet[row][col] !== '') {
                    values.push(sheet[row][col]);
                  }
                }
              }
              value = values.length > 1 ? values : values[0];
            }
            break;
            
          case 'column':
            const colIndex = parseColumnLetter(dataLocation.coordinates);
            if (colIndex !== null) {
              const columnValues = [];
              for (let row = 1; row < sheet.length; row++) {
                if (sheet[row] && sheet[row][colIndex] !== undefined && sheet[row][colIndex] !== '') {
                  columnValues.push(sheet[row][colIndex]);
                }
              }
              value = columnValues.length > 1 ? columnValues : columnValues[0];
            }
            break;
            
          case 'row':
            const rowIndex = parseInt(dataLocation.coordinates.replace('row', ''));
            if (sheet[rowIndex]) {
              const rowValues = sheet[rowIndex].filter(cell => cell !== undefined && cell !== '');
              value = rowValues.length > 1 ? rowValues : rowValues[0];
            }
            break;
        }
        
        if (value !== null) {
          extractedData[fieldName] = value;
          console.log(`✅ Extracted ${fieldName}:`, value);
        } else {
          console.warn(`❌ Could not extract value for ${fieldName}`);
        }
      });
      
      return {
        extractedData,
        confidence: mappingResult.overallConfidence,
        recommendations: mappingResult.recommendations
      };
    } catch (error) {
      console.error('❌ Data extraction failed:', error);
      throw error;
    }
  };

  // Helper functions for coordinate parsing
  const parseCellCoordinate = (coordinate) => {
    const match = coordinate.match(/^([A-Z]+)(\d+)$/);
    if (match) {
      const col = parseColumnLetter(match[1]);
      const row = parseInt(match[2]) - 1;
      return { row, col };
    }
    return null;
  };

  const parseRangeCoordinate = (range) => {
    const match = range.match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/);
    if (match) {
      const startCol = parseColumnLetter(match[1]);
      const startRow = parseInt(match[2]) - 1;
      const endCol = parseColumnLetter(match[3]);
      const endRow = parseInt(match[4]) - 1;
      return { startRow, startCol, endRow, endCol };
    }
    return null;
  };

  const parseColumnLetter = (letters) => {
    let result = 0;
    for (let i = 0; i < letters.length; i++) {
      result = result * 26 + (letters.charCodeAt(i) - 64);
    }
    return result - 1;
  };

  // Transform sheet data to Markdown table
  const sheetToMarkdownTable = (sheetData) => {
    if (!sheetData || !sheetData.length) return '';
    const header = sheetData[0];
    const rows = sheetData.slice(1);
    const headerLine = '| ' + header.join(' | ') + ' |';
    const separator = '| ' + header.map(() => '---').join(' | ') + ' |';
    const rowLines = rows.map(row => '| ' + row.map(cell => (cell !== undefined ? String(cell) : '')).join(' | ') + ' |');
    return [headerLine, separator, ...rowLines].join('\n');
  };

  // Generate preview data for Excel analysis
  const generateExcelPreviewData = (sheetData, sheetName) => {
    if (!sheetData || !sheetData.length) return null;
    
    const headers = sheetData[0] || [];
    const dataRows = sheetData.slice(1);
    
    const columnAnalysis = headers.map((header, colIndex) => {
      if (!header || typeof header !== 'string') return null;
      
      const columnValues = [];
      for (let rowIndex = 0; rowIndex < Math.min(10, dataRows.length); rowIndex++) {
        const row = dataRows[rowIndex];
        if (row && row[colIndex] !== undefined && row[colIndex] !== '') {
          columnValues.push(String(row[colIndex]).trim());
        }
      }
      
      let formFieldDefinitions = [];
      if (isSingleTableMode) {
        formFieldDefinitions = columns.map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text'
        }));
      } else if (isDualTableMode) {
        const parentFields = (parentConfig?.columns || []).map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          tableType: 'parent'
        }));
        const childFields = (childConfig?.columns || []).map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          tableType: 'child'
        }));
        formFieldDefinitions = [...parentFields, ...childFields];
      } else if (isTableViewMode) {
        formFieldDefinitions = columns.map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text'
        }));
      }
      
      const bestMatch = findBestFieldMatch(header.trim(), formFieldDefinitions);
      
      return {
        excelColumn: header.trim(),
        columnIndex: colIndex,
        sampleValues: columnValues.slice(0, 5),
        totalValues: columnValues.length,
        matchedField: bestMatch ? {
          name: bestMatch.name,
          displayName: bestMatch.displayName,
          type: bestMatch.type,
          confidence: bestMatch.confidence,
          tableType: bestMatch.tableType || 'single'
        } : null
      };
    }).filter(Boolean);
    
    return {
      sheetName,
      totalRows: dataRows.length,
      totalColumns: headers.length,
      detectedColumns: columnAnalysis,
      hasHeaders: headers.some(h => h && typeof h === 'string' && h.trim()),
      dataPreview: {
        headers,
        sampleRows: dataRows.slice(0, 5)
      }
    };
  };

  // Find best matching form field for a file field/label
  const findBestFieldMatch = (fileField, formFields) => {
    const normalizedFileField = fileField.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    let bestMatch = null;
    let highestScore = 0;
    
    formFields.forEach(formField => {
      const normalizedFormName = formField.name.toLowerCase().replace(/[^a-z0-9]/g, '');
      const normalizedFormLabel = (formField.label || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      
      const nameScore = calculateSimilarity(normalizedFileField, normalizedFormName);
      const labelScore = calculateSimilarity(normalizedFileField, normalizedFormLabel);
      const maxScore = Math.max(nameScore, labelScore);
      
      if (maxScore > highestScore && maxScore > 0.5) {
        highestScore = maxScore;
        bestMatch = {
          ...formField,
          confidence: maxScore
        };
      }
    });
    
    return bestMatch;
  };

  // Calculate string similarity
  const calculateSimilarity = (str1, str2) => {
    if (str1 === str2) return 1;
    if (str1.length === 0 || str2.length === 0) return 0;
    
    if (str1.includes(str2) || str2.includes(str1)) {
      return 0.8;
    }
    
    const chars1 = new Set(str1);
    const chars2 = new Set(str2);
    const intersection = new Set([...chars1].filter(x => chars2.has(x)));
    const union = new Set([...chars1, ...chars2]);
    
    return intersection.size / union.size;
  };

  // Debug Panel Component
  const DebugPanel = ({ debugInfo, onClose }) => {
    const [activeTab, setActiveTab] = useState('input');
    
    if (!debugInfo) return null;

    return (
      <div className="debug-panel-overlay">
        <div className="debug-panel">
          <div className="debug-panel-header">
            <h3>🔍 AI Extraction Debug Review</h3>
            <div className="debug-panel-meta">
              <span className="debug-timestamp">
                {new Date(debugInfo.timestamp).toLocaleString()}
              </span>
              <span className="debug-method">
                Method: {debugInfo.method}
              </span>
              {debugInfo.confidence && (
                <span className="debug-confidence">
                  Confidence: {(debugInfo.confidence * 100).toFixed(1)}%
                </span>
              )}
            </div>
            <button 
              className="debug-close-btn"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
          
          <div className="debug-panel-tabs">
            <button 
              className={`debug-tab ${activeTab === 'input' ? 'active' : ''}`}
              onClick={() => setActiveTab('input')}
            >
              📤 Input Message
            </button>
            <button 
              className={`debug-tab ${activeTab === 'output' ? 'active' : ''}`}
              onClick={() => setActiveTab('output')}
            >
              📥 AI Response
            </button>
            <button 
              className={`debug-tab ${activeTab === 'extracted' ? 'active' : ''}`}
              onClick={() => setActiveTab('extracted')}
            >
              📊 Extracted Data
            </button>
            {debugInfo.metadata && (
              <button 
                className={`debug-tab ${activeTab === 'metadata' ? 'active' : ''}`}
                onClick={() => setActiveTab('metadata')}
              >
                📋 Metadata
              </button>
            )}
          </div>
          
          <div className="debug-panel-content">
            {activeTab === 'input' && (
              <div className="debug-section">
                <h4>Input Message Sent to AI:</h4>
                <div className="debug-content-box">
                  <pre>{debugInfo.inputMessage}</pre>
                </div>
                {debugInfo.fieldDefinitions && (
                  <div className="debug-field-definitions">
                    <h4>Field Definitions:</h4>
                    <div className="debug-content-box">
                      <pre>{JSON.stringify(debugInfo.fieldDefinitions, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'output' && (
              <div className="debug-section">
                <h4>Raw AI Response:</h4>
                <div className="debug-content-box">
                  <pre>{JSON.stringify(debugInfo.rawResponse, null, 2)}</pre>
                </div>
              </div>
            )}
            
            {activeTab === 'extracted' && (
              <div className="debug-section">
                <h4>Processed Extracted Data:</h4>
                <div className="debug-content-box">
                  <pre>{JSON.stringify(debugInfo.extractedData, null, 2)}</pre>
                </div>
                {debugInfo.fuzzyMatches && debugInfo.fuzzyMatches.length > 0 && (
                  <div className="debug-fuzzy-matches">
                    <h4>🔍 Fuzzy Matches Found:</h4>
                    <div className="debug-content-box">
                      <table className="fuzzy-matches-table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Original Path</th>
                            <th>Similarity</th>
                            <th>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {debugInfo.fuzzyMatches.map((match, index) => (
                            <tr key={index}>
                              <td>{match.field}</td>
                              <td>{match.originalPath}</td>
                              <td>{(match.similarity * 100).toFixed(1)}%</td>
                              <td>{String(match.value).substring(0, 50)}...</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {debugInfo.extractionNotes && (
                  <div className="debug-notes">
                    <h4>Extraction Notes:</h4>
                    <div className="debug-content-box">
                      <pre>{JSON.stringify(debugInfo.extractionNotes, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'metadata' && debugInfo.metadata && (
              <div className="debug-section">
                <h4>Extraction Metadata:</h4>
                <div className="debug-content-box">
                  <pre>{JSON.stringify(debugInfo.metadata, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
          
          <div className="debug-panel-footer">
            <div className="debug-stats">
              <span>File: {debugInfo.fileName}</span>
              <span>Fields Processed: {debugInfo.fieldCount || 'N/A'}</span>
              <span>Processing Time: {debugInfo.processingTime || 'N/A'}ms</span>
            </div>
            {debugInfo.tokenUsage && (
              <div className="debug-token-summary">
                <h4>🔢 Token Summary</h4>
                <div className="token-stats">
                  <div className="token-stat">
                    <span className="token-label">Input:</span>
                    <span className="token-value">{debugInfo.tokenUsage.promptTokens.toLocaleString()}</span>
                  </div>
                  <div className="token-stat">
                    <span className="token-label">Output:</span>
                    <span className="token-value">{debugInfo.tokenUsage.completionTokens.toLocaleString()}</span>
                  </div>
                  <div className="token-stat total">
                    <span className="token-label">Total:</span>
                    <span className="token-value">{debugInfo.tokenUsage.totalTokens.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };



  // Proceed with OpenAI analysis after preview confirmation
  const handleProceedWithOpenAIAnalysis = async () => {
    setShowOpenAIDataPreview(false);
    setIsProcessingOpenAI(true);
    try {
      await handleOpenAISendToOpenAI(openAISheetText);
    } catch (error) {
      console.error('OpenAI Excel analysis failed:', error.message);
    } finally {
      setIsProcessingOpenAI(false);
    }
  };

  // When user selects a sheet, show data preview first
  const handleOpenAISheetSelect = async (sheetName) => {
    setOpenAISelectedSheet(sheetName);
    setShowOpenAISheetSelector(false);
    setIsProcessingOpenAI(true);
    try {
      const worksheet = currentWorkbook.Sheets[sheetName];
      const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      
      const previewData = generateExcelPreviewData(sheetData, sheetName);
      setOpenAIPreviewData(previewData);
      setShowOpenAIDataPreview(true);
      
      const markdown = sheetToMarkdownTable(sheetData);
      setOpenAISheetText(markdown);
    } catch (error) {
      setIsProcessingOpenAI(false);
      console.error('Failed to process selected sheet:', error.message);
    } finally {
      setIsProcessingOpenAI(false);
    }
  };

  // Send the Markdown table to OpenAI
  const handleOpenAISendToOpenAI = async (sheetText) => {
    try {
      let formFieldDefinitions = [];
      
      if (isSingleTableMode) {
        formFieldDefinitions = columns.map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'single',
          extractionMode: 'single_value',
          unit: col.unit,
          tableContext: {
            tableName: tableName,
            mode: 'single_table',
            aiInstructions: tableConfig?.aiInstructions || ''
          }
        }));
      } else if (isDualTableMode) {
        const parentFields = (parentConfig?.columns || []).map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'single',
          extractionMode: 'single_value',
          unit: col.unit,
          tableType: 'parent',
          tableContext: {
            tableName: tables?.parent || 'parent',
            mode: 'dual_table_parent',
            relationship: 'one_to_many_parent',
            aiInstructions: parentConfig?.aiInstructions || ''
          }
        }));
        
        const childFields = (childConfig?.columns || []).map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'multiple',
          extractionMode: 'table_rows',
          unit: col.unit,
          tableType: 'child',
          tableContext: {
            tableName: tables?.child || 'child',
            mode: 'dual_table_child',
            relationship: 'one_to_many_child',
            parentTable: tables?.parent || 'parent',
            foreignKey: matchKey?.childForeignKey,
            parentKey: matchKey?.parentKey,
            aiInstructions: childConfig?.aiInstructions || ''
          },
          grouping: {
            groupBy: 'table_row',
            extractAsGroup: true
          }
        }));
        
        formFieldDefinitions = [...parentFields, ...childFields];
      } else if (isTableViewMode) {
        formFieldDefinitions = columns.map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'multiple',
          extractionMode: 'table_rows',
          unit: col.unit,
          tableContext: {
            tableName: sectionConfig?.tableName || tableName,
            mode: 'table_view',
            relationship: 'table_rows',
            aiInstructions: sectionConfig?.aiInstructions || tableConfig?.aiInstructions || ''
          },
          grouping: {
            groupBy: 'table_row',
            extractAsGroup: true
          }
        }));
      }

      // Optimize field definitions to reduce duplication
      const optimizedFields = optimizeFieldDefinitions(formFieldDefinitions);

      // Create optimized prompt with reduced duplication
      const prompt = `You are an expert data extraction assistant. Analyze the following Excel sheet and extract data according to the field specifications.

**Excel Sheet Data:**
${sheetText}

**FIELD GROUPS:**

**Single Value Fields (extract one value per field):**
${optimizedFields.fieldGroups.single.length > 0 ? JSON.stringify(optimizedFields.fieldGroups.single, null, 2) : 'None'}

**Multiple Value Fields (extract as table rows):**
${optimizedFields.fieldGroups.multiple.length > 0 ? JSON.stringify(optimizedFields.fieldGroups.multiple, null, 2) : 'None'}

${optimizedFields.aiInstructions ? `**ADMIN INSTRUCTIONS:**
${optimizedFields.aiInstructions}

` : ''}

**EXTRACTION RULES:**
1. **Single fields**: Extract one value per field, return as string/number
2. **Multiple fields**: Extract complete table rows with all related fields together
3. **Grouped fields**: Keep related fields together in the same row object
4. **Table data**: Maintain relationships between fields in the same table row

**RESPONSE FORMAT:**
{
  "extractedData": {
    // Single value fields
    "fieldName": "single_value",
    
    // Multiple value fields (for BHA components, equipment lists, etc.)
    "fieldName": [
      {
        "field1": "value1_row1",
        "field2": "value2_row1",
        "field3": "value3_row1"
      },
      {
        "field1": "value1_row2", 
        "field2": "value2_row2",
        "field3": "value3_row2"
      }
    ]
  },
  "confidence": 0.95,
  "extractionNotes": {
    "fieldGroupings": "How fields were grouped",
    "tableRowsExtracted": "Number of complete rows extracted",
    "singleValuesExtracted": "Number of single values extracted"
  }
}

**IMPORTANT:**
- For table data (like BHA components), extract complete rows with all related fields
- Use exact field names from the definitions
- Group related fields together when extractAsGroup is true
- Maintain data relationships between fields in the same table row`;

      const startTime = Date.now();
      
      const response = await fetch('/api/openai/analyze-excel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          prompt, 
          excelData: {}, 
          fieldDefinitions: formFieldDefinitions,
          extractionContext: {
            mode: isSingleTableMode ? 'single_table' : isDualTableMode ? 'dual_table' : 'table_view',
            hasGroupedFields: formFieldDefinitions.some(f => f.grouping?.extractAsGroup),
            hasMultipleValueFields: formFieldDefinitions.some(f => f.cardinality === 'multiple'),
            tableRelationships: formFieldDefinitions.filter(f => f.tableContext?.relationship)
          }
        })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        let errorData;
        
        try {
          if (contentType && contentType.includes('application/json')) {
            errorData = await response.json();
          } else {
            const htmlText = await response.text();
            console.error('❌ Excel analysis: Received HTML response instead of JSON:', htmlText.substring(0, 500));
            const titleMatch = htmlText.match(/<title>(.*?)<\/title>/i);
            const errorTitle = titleMatch ? titleMatch[1] : 'Unknown Error';
            throw new Error(`Server returned HTML error page: ${errorTitle} (Status: ${response.status})`);
          }
        } catch (parseError) {
          console.error('❌ Excel analysis: Failed to parse error response:', parseError);
          throw new Error(`Server error (Status: ${response.status}). Check if backend server is running.`);
        }
        
        throw new Error('OpenAI API request failed: ' + (errorData.error || response.status));
      }

      const result = await response.json();
      const endTime = Date.now();
      
      console.log('🤖 Enhanced OpenAI response:', result);
      
      // Process the extracted data to handle table structures properly
      const processedData = processTableDataExtraction(result.extractedData || {}, formFieldDefinitions);
      
      // Store debug information
      const debugData = {
        timestamp: new Date().toISOString(),
        method: 'Excel Analysis',
        fileName: droppedFile?.name || 'Excel Sheet',
        inputMessage: prompt,
        fieldDefinitions: optimizedFields.fieldGroups,
        rawResponse: result,
        extractedData: processedData,
        extractionNotes: result.extractionNotes,
        confidence: result.confidence,
        metadata: result.metadata,
        fieldCount: optimizedFields.totalFields,
        processingTime: endTime - startTime
      };
      
      updateDebugInfo(debugData);
      
      // Update the result with processed data
      const enhancedResult = {
        ...result,
        extractedData: processedData,
        originalExtractedData: result.extractedData
      };
      
      setOpenAIResults(enhancedResult);
      setShowOpenAIExtractor(true);
    } catch (error) {
      console.error('OpenAI Excel analysis failed:', error.message);
      setShowOpenAIExtractor(false);
    } finally {
      setIsProcessingOpenAI(false);
    }
  };

  // Handle OpenAI Excel Extractor
  const handleOpenAIExcelExtraction = useCallback(async () => {
    console.log('🔥 DEBUG: handleOpenAIExcelExtraction started');
    console.log('🔥 DEBUG: droppedFile:', droppedFile?.name, droppedFile?.size);
    
    if (!droppedFile) {
      console.log('❌ DEBUG: No dropped file found in OpenAI extraction');
      return;
    }
    
    console.log('🔥 DEBUG: About to call prepareFileForExtraction for OpenAI');
    try {
      const shouldProceed = await prepareFileForExtraction(droppedFile, 'openai');
      console.log('🔥 DEBUG: prepareFileForExtraction returned:', shouldProceed);
      
      if (!shouldProceed) {
        console.log('🔥 DEBUG: prepareFileForExtraction returned false, not proceeding with OpenAI');
        return;
      }
      
      console.log('🔥 DEBUG: Setting OpenAI processing states');
      setIsProcessingOpenAI(true);
      setShowOpenAIExtractor(false);
      setShowOpenAISheetSelector(false);
      
      try {
        // Use the selected sheet or first sheet
        const sheetName = selectedExcelSheet || currentWorkbook.SheetNames[0];
        console.log('🔥 DEBUG: Using sheet for OpenAI:', sheetName);
        console.log('🔥 DEBUG: Available sheets:', currentWorkbook.SheetNames);
        setOpenAISheetNames(currentWorkbook.SheetNames);
        setShowOpenAISheetSelector(true);
        console.log('🔥 DEBUG: OpenAI sheet selector shown');
      } catch (error) {
        console.error('❌ DEBUG: Error in OpenAI sheet processing:', error);
        setIsProcessingOpenAI(false);
        setShowOpenAISheetSelector(false);
        setShowOpenAIExtractor(false);
        console.error('Failed to read Excel file:', error.message);
      }
    } catch (error) {
      console.error('❌ DEBUG: Error in handleOpenAIExcelExtraction:', error);
    }
  }, [droppedFile, selectedExcelSheet, currentWorkbook]);

  // Handle OpenAI Excel extraction with specific sheet
  const handleOpenAIExcelExtractionWithSheet = useCallback(async (sheetName) => {
    if (!currentWorkbook) return;
    
    setIsProcessingOpenAI(true);
    setShowOpenAIExtractor(false);
    setShowOpenAISheetSelector(false);
    
    try {
      setOpenAISheetNames(currentWorkbook.SheetNames);
      setShowOpenAISheetSelector(true);
    } catch (error) {
      setIsProcessingOpenAI(false);
      setShowOpenAISheetSelector(false);
      setShowOpenAIExtractor(false);
      console.error('Failed to process Excel file:', error.message);
    }
  }, [currentWorkbook]);

  // Handle OpenAI extraction results
  const handleOpenAIExtractionResults = useCallback((applyResults) => {
    if (!openAIResults?.extractedData) return;
    
    try {
      const { extractedData } = openAIResults;
      console.log('🤖 Applying OpenAI extraction results:', extractedData);
      
      if (onDataExtracted) {
        onDataExtracted({
          type: 'openai',
          data: extractedData,
          confidence: openAIResults.confidence,
          recommendations: openAIResults.recommendations
        });
      }
      
      // Close modals
      setShowOpenAIExtractor(false);
      setOpenAIResults(null);
      // Don't call onFileProcessed() here - let user perform more extractions if needed
      
    } catch (error) {
      console.error('❌ Failed to apply OpenAI extraction results:', error);
    }
  }, [openAIResults, onDataExtracted, onFileProcessed]);

  // Check if form data is saved (has primary key)
  const isFormSaved = () => {
    const primaryKey = Object.keys(formData).find(key => 
      key.toLowerCase().includes('id') && formData[key]
    );
    return !!primaryKey && !!formData[primaryKey];
  };

  // Handle file option selection
  const handleSaveAsAttachment = () => {
    if (!isFormSaved()) {
      alert('Please save the form before adding attachments.');
      setShowFileOptions(false);
      return;
    }
    setShowFileOptions(false);
    setShowAttachmentForm(true);
  };

  const handleReadToForm = useCallback(async () => {
    console.log('🔥 DEBUG: handleReadToForm started');
    console.log('🔥 DEBUG: droppedFile:', droppedFile?.name, droppedFile?.size);
    
    if (!droppedFile) {
      console.log('❌ DEBUG: No dropped file found in Read to Form');
      return;
    }
    
    console.log('🔥 DEBUG: About to call prepareFileForExtraction for Read to Form');
    try {
      const shouldProceed = await prepareFileForExtraction(droppedFile, 'readToForm');
      console.log('🔥 DEBUG: prepareFileForExtraction returned:', shouldProceed);
      
      if (!shouldProceed) {
        console.log('🔥 DEBUG: prepareFileForExtraction returned false, not proceeding with Read to Form');
        return;
      }
      
      console.log('🔥 DEBUG: Closing file options and starting processing');
      setShowFileOptions(false);
      setIsProcessingFile(true);
      
      try {
        console.log('🔄 Extracting data from file:', droppedFile.name);
        
        if (needsSelection(droppedFile) === 'excel') {
          console.log('🔥 DEBUG: Processing Excel file with sheet:', selectedExcelSheet);
          await handleReadToFormWithSheet(selectedExcelSheet);
        } else if (needsSelection(droppedFile) === 'pdf') {
          console.log('🔥 DEBUG: Processing PDF file with page:', selectedPDFPage);
          await handleReadToFormWithPDFPage(selectedPDFPage);
        } else {
          console.log('🔥 DEBUG: Processing generic file');
          await handleReadToFormGeneric();
        }
        console.log('🔥 DEBUG: Read to Form processing completed');
      } catch (err) {
        console.error('❌ DEBUG: Extraction error in Read to Form:', err);
        alert('Failed to extract data from file');
      } finally {
        console.log('🔥 DEBUG: Read to Form cleanup');
        setIsProcessingFile(false);
        // Don't call onFileProcessed() here - let user perform more extractions if needed
      }
    } catch (error) {
      console.error('❌ DEBUG: Error in handleReadToForm:', error);
    }
  }, [droppedFile, selectedExcelSheet, selectedPDFPage]);

  // Handle read to form with specific Excel sheet
  const handleReadToFormWithSheet = async (sheetName) => {
    if (!currentWorkbook || !sheetName) return;
    
    try {
      const worksheet = currentWorkbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

      const formFieldDefs = isTableViewMode
        ? sectionConfig.fields.filter(f => f.enabled).map(f => ({ name: f.dbColumn, label: f.name }))
        : columns.map(c => ({ name: c.name, label: c.label || c.displayName || c.name }));

      let mode = 'labelValue';
      let headerRowIndex = -1;
      for (let r = 0; r < Math.min(10, rows.length); r++) {
        let matchCount = 0;
        rows[r].forEach(cell => {
          if (typeof cell === 'string' && findBestFieldMatch(cell.trim(), formFieldDefs)) {
            matchCount++;
          }
        });
        if (matchCount >= 2) { mode = 'table'; headerRowIndex = r; break; }
      }

      if (mode === 'labelValue') {
        const extracted = {};
        rows.forEach((row, r) => {
          row.forEach((cell, c) => {
            if (typeof cell === 'string' && cell.trim()) {
              const match = findBestFieldMatch(cell.trim(), formFieldDefs);
              if (match && !extracted[match.name]) {
                let val = '';
                if (c + 1 < row.length && rows[r][c+1] !== '') val = rows[r][c+1];
                else if (r + 1 < rows.length) val = rows[r+1][c];
                extracted[match.name] = val;
              }
            }
          });
        });
        
        if (onDataExtracted) {
          onDataExtracted({
            type: 'readToForm',
            data: extracted,
            mode: 'labelValue'
          });
        }
      } else {
        const header = rows[headerRowIndex];
        const headerMap = {};
        header.forEach((cell, c) => {
          if (typeof cell === 'string') {
            const match = findBestFieldMatch(cell.trim(), formFieldDefs);
            if (match) headerMap[c] = match.name;
          }
        });
        const tableData = [];
        for (let r = headerRowIndex + 1; r < rows.length; r++) {
          const row = rows[r];
          if (row.every(v => v === '')) break;
          const rec = {};
          Object.entries(headerMap).forEach(([c, name]) => {
            rec[name] = row[c] !== undefined ? row[c] : '';
          });
          tableData.push(rec);
        }
        
        if (onDataExtracted) {
          onDataExtracted({
            type: 'readToForm',
            data: tableData,
            mode: 'table'
          });
        }
      }
    } catch (error) {
      console.error('Failed to process Excel sheet:', error);
      throw error;
    }
  };

  // Handle read to form with specific PDF page
  const handleReadToFormWithPDFPage = async (pageNumber) => {
    // For PDF, we'll use the existing logic but could be enhanced to extract specific pages
    // For now, we'll use the generic handler
    await handleReadToFormGeneric();
  };

  // Generic read to form handler for non-Excel/PDF files
  const handleReadToFormGeneric = async () => {
    try {
      console.log('🔄 Extracting data from file:', droppedFile.name);
      const arrayBuffer = await droppedFile.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

      const formFieldDefs = isTableViewMode
        ? sectionConfig.fields.filter(f => f.enabled).map(f => ({ name: f.dbColumn, label: f.name }))
        : columns.map(c => ({ name: c.name, label: c.label || c.displayName || c.name }));

      let mode = 'labelValue';
      let headerRowIndex = -1;
      for (let r = 0; r < Math.min(10, rows.length); r++) {
        let matchCount = 0;
        rows[r].forEach(cell => {
          if (typeof cell === 'string' && findBestFieldMatch(cell.trim(), formFieldDefs)) {
            matchCount++;
          }
        });
        if (matchCount >= 2) { mode = 'table'; headerRowIndex = r; break; }
      }

      if (mode === 'labelValue') {
        const extracted = {};
        rows.forEach((row, r) => {
          row.forEach((cell, c) => {
            if (typeof cell === 'string' && cell.trim()) {
              const match = findBestFieldMatch(cell.trim(), formFieldDefs);
              if (match && !extracted[match.name]) {
                let val = '';
                if (c + 1 < row.length && rows[r][c+1] !== '') val = rows[r][c+1];
                else if (r + 1 < rows.length) val = rows[r+1][c];
                extracted[match.name] = val;
              }
            }
          });
        });
        
        if (onDataExtracted) {
          onDataExtracted({
            type: 'readToForm',
            data: extracted,
            mode: 'labelValue'
          });
        }
      } else {
        const header = rows[headerRowIndex];
        const headerMap = {};
        header.forEach((cell, c) => {
          if (typeof cell === 'string') {
            const match = findBestFieldMatch(cell.trim(), formFieldDefs);
            if (match) headerMap[c] = match.name;
          }
        });
        const tableData = [];
        for (let r = headerRowIndex + 1; r < rows.length; r++) {
          const row = rows[r];
          if (row.every(v => v === '')) break;
          const rec = {};
          Object.entries(headerMap).forEach(([c, name]) => {
            rec[name] = row[c] !== undefined ? row[c] : '';
          });
          tableData.push(rec);
        }
        
        if (onDataExtracted) {
          onDataExtracted({
            type: 'readToForm',
            data: tableData,
            mode: 'table'
          });
        }
      }
    } catch (err) {
      console.error('❌ Extraction error:', err);
      throw err;
    }
  };

  // Handle Excel mapping results
  const handleExcelDataExtracted = (extractionResults) => {
    console.log('📊 Excel data extracted:', extractionResults);
    
    if (extractionResults?.results?.mappings) {
      const mappings = extractionResults.results.mappings;
      
      if (onDataExtracted) {
        onDataExtracted({
          type: 'excel',
          data: mappings,
          extractionResults
        });
      }
    }
    
    setShowExcelMapping(false);
    // Don't call onFileProcessed() here - let user perform more extractions if needed
  };

  // Handle Smart Excel Extractor results
  const handleSmartExcelDataExtracted = (extractionResults) => {
    console.log('🚀 Smart Excel extraction results received:', extractionResults);
    
    if (!extractionResults || !extractionResults.mappedData) {
      console.warn('No Smart Excel extraction results found');
      return;
    }

    try {
      const { mappedData, extractedFields, confidence } = extractionResults;
      console.log(`Smart extraction confidence: ${(confidence * 100).toFixed(1)}%`);
      
      if (onDataExtracted) {
        onDataExtracted({
          type: 'smartExcel',
          data: mappedData,
          confidence,
          extractedFields
        });
      }
      
      setShowSmartExcelExtractor(false);
      // Don't call onFileProcessed() here - let user perform more extractions if needed
      
      console.log(`✅ Smart Excel extraction completed with ${Object.keys(mappedData).length} fields mapped`);
      
    } catch (error) {
      console.error('Error applying Smart Excel extraction results:', error);
    }
  };

  // Show file options modal
  const showFileOptionsModal = () => {
    console.log('🔥 DEBUG: showFileOptionsModal called');
    setShowFileOptions(true);
  };

  // Auto-show file options modal when file is dropped
  useEffect(() => {
    if (droppedFile && !showFileOptions) {
      console.log('🔥 DEBUG: File detected, showing options modal:', droppedFile.name);
      console.log('🔥 DEBUG: File size:', droppedFile.size);
      console.log('🔥 DEBUG: File type:', droppedFile.type);
      setShowFileOptions(true);
    }
  }, [droppedFile, showFileOptions]);

  // Hide all modals and reset state
  const hideAllModals = () => {
    setShowFileOptions(false);
    setShowAttachmentForm(false);
    setShowDataMapping(false);
    setShowEnhancedMapping(false);
    setShowExcelMapping(false);
    setShowSmartExcelExtractor(false);
    setShowOpenAIExtractor(false);
    setShowOpenAISheetSelector(false);
    setShowOpenAIDataPreview(false);
    setMappedData(null);
    setEnhancedParsedData(null);
    setOpenAIResults(null);
    setExcelSheets(null);
    setSelectedSheet(null);
    setCurrentWorkbook(null);
    setOpenAISheetNames([]);
    setOpenAISelectedSheet(null);
    setOpenAISheetText('');
    setOpenAIPreviewData(null);
    onFileProcessed();
  };

  // Helper function to check backend connectivity
  const checkBackendConnectivity = async () => {
    try {
      const response = await fetch('/api/openai/test-vision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        console.log('✅ Backend connectivity check passed');
        return true;
      } else {
        console.warn('⚠️ Backend connectivity check failed:', response.status);
        return false;
      }
    } catch (error) {
      console.error('❌ Backend connectivity check error:', error);
      return false;
    }
  };

  // Add image compression utility function
  const compressImage = (file, maxWidth = 1200, maxHeight = 1600, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob with compression
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Add file size formatting utility
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleImageExtraction = async (file, pageNumber = null) => {
    console.log('🖼️ Starting image extraction for:', file.name);
    if (pageNumber) {
      console.log('📄 Processing PDF page:', pageNumber);
    }
    console.log('📏 Original file size:', formatFileSize(file.size));
    
    try {
      setExtractionStatus('processing');
      setExtractionProgress(10);
      
      // Check backend connectivity first
      const isBackendConnected = await checkBackendConnectivity();
      if (!isBackendConnected) {
        throw new Error('Backend server is not responding. Please check if the server is running on port 3001.');
      }
      
      setExtractionProgress(20);
      
      // Compress image if it's too large (over 2MB for base64 encoding)
      let processedFile = file;
      const maxFileSize = 2 * 1024 * 1024; // 2MB (base64 encoding increases size by ~33%)
      
      if (file.size > maxFileSize) {
        console.log('🗜️ File size exceeds 2MB, compressing...');
        setExtractionStatus('compressing');
        setExtractionProgress(30);
        
        const compressedBlob = await compressImage(file, 1000, 1200, 0.6);
        processedFile = new File([compressedBlob], file.name, { type: 'image/jpeg' });
        
        console.log('✅ Image compressed:', {
          originalSize: formatFileSize(file.size),
          compressedSize: formatFileSize(processedFile.size),
          compressionRatio: `${Math.round((1 - processedFile.size / file.size) * 100)}%`
        });
        setExtractionStatus('processing');
      }
      
      setExtractionProgress(40);
      
      // Generate field definitions for the current form mode
      let fieldDefinitions = [];
      
      if (isSingleTableMode) {
        fieldDefinitions = columns.map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'single',
          extractionMode: 'single_value',
          unit: col.unit,
          tableContext: {
            tableName: tableName,
            mode: 'single_table',
            aiInstructions: tableConfig?.aiInstructions || ''
          }
        }));
      } else if (isDualTableMode) {
        const parentFields = (parentConfig?.columns || []).map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'single',
          extractionMode: 'single_value',
          unit: col.unit,
          tableType: 'parent',
          tableContext: {
            tableName: tables?.parent || 'parent',
            mode: 'dual_table_parent',
            relationship: 'one_to_many_parent',
            aiInstructions: parentConfig?.aiInstructions || ''
          }
        }));
        
        const childFields = (childConfig?.columns || []).map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'multiple',
          extractionMode: 'table_rows',
          unit: col.unit,
          tableType: 'child',
          tableContext: {
            tableName: tables?.child || 'child',
            mode: 'dual_table_child',
            relationship: 'one_to_many_child',
            parentTable: tables?.parent || 'parent',
            foreignKey: matchKey?.childForeignKey,
            parentKey: matchKey?.parentKey,
            aiInstructions: childConfig?.aiInstructions || ''
          },
          grouping: {
            groupBy: 'table_row',
            extractAsGroup: true
          }
        }));
        
        fieldDefinitions = [...parentFields, ...childFields];
      } else if (isTableViewMode) {
        fieldDefinitions = columns.map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'single',
          extractionMode: 'single_value',
          unit: col.unit,
          tableContext: {
            tableName: sectionConfig?.tableName || 'table',
            mode: 'table_view',
            aiInstructions: sectionConfig?.aiInstructions || ''
          }
        }));
      }
      
      // Convert to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      
      reader.readAsDataURL(processedFile);
      const imageData = await base64Promise;
      
      setExtractionProgress(60);
      
      // Send to AI with page number if provided
      const requestBody = {
        imageData: imageData,
        fieldDefinitions: fieldDefinitions,
        fileName: file.name
      };
      
      if (pageNumber) {
        requestBody.pageNumber = pageNumber;
      }
      
      const response = await fetch('/api/openai/analyze-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      setExtractionProgress(80);
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.details || errorMessage;
          } else {
            // Handle HTML error pages
            const htmlText = await response.text();
            const titleMatch = htmlText.match(/<title>(.*?)<\/title>/i);
            const errorTitle = titleMatch ? titleMatch[1] : 'Unknown Error';
            errorMessage = `Server returned HTML error page: ${errorTitle} (Status: ${response.status})`;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      setExtractionProgress(100);
      
      console.log('✅ Image extraction completed:', {
        confidence: result.confidence,
        extractedFields: Object.keys(result.extractedData || {}).length,
        fileSize: formatFileSize(processedFile.size),
        compressionUsed: file.size !== processedFile.size,
        pageNumber: pageNumber
      });
      
      return result;
      
    } catch (error) {
      console.error('❌ Image extraction failed:', error);
      setExtractionStatus('error');
      throw error;
    } finally {
      setExtractionStatus('idle');
      setExtractionProgress(0);
    }
  };

  // Handle AI Image Extractor button click
  const handleAIImageExtractor = async () => {
    if (!droppedFile) return;
    
    const shouldProceed = await prepareFileForExtraction(droppedFile, 'ai');
    if (!shouldProceed) return;
    
    try {
      setIsProcessingOpenAI(true);
      
      if (needsSelection(droppedFile) === 'pdf') {
        await handleAIImageExtractorWithPage(selectedPDFPage);
      } else {
        await handleAIImageExtractorGeneric();
      }
    } catch (error) {
      console.error('AI Image extraction failed:', error);
      setIsProcessingOpenAI(false);
    }
  };

  // Handle AI Image extraction with specific PDF page
  const handleAIImageExtractorWithPage = async (pageNumber) => {
    try {
      const result = await handleImageExtraction(droppedFile, pageNumber);
      
      // Generate field definitions for the current form mode
      let fieldDefinitions = [];
      
      if (isSingleTableMode) {
        fieldDefinitions = columns.map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'single',
          extractionMode: 'single_value',
          unit: col.unit,
          tableContext: {
            tableName: tableName,
            mode: 'single_table',
            aiInstructions: tableConfig?.aiInstructions || ''
          }
        }));
      } else if (isDualTableMode) {
        const parentFields = (parentConfig?.columns || []).map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'single',
          extractionMode: 'single_value',
          unit: col.unit,
          tableType: 'parent',
          tableContext: {
            tableName: tables?.parent || 'parent',
            mode: 'dual_table_parent',
            relationship: 'one_to_many_parent',
            aiInstructions: parentConfig?.aiInstructions || ''
          }
        }));
        
        const childFields = (childConfig?.columns || []).map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'multiple',
          extractionMode: 'table_rows',
          unit: col.unit,
          tableType: 'child',
          tableContext: {
            tableName: tables?.child || 'child',
            mode: 'dual_table_child',
            relationship: 'one_to_many_child',
            parentTable: tables?.parent || 'parent',
            foreignKey: matchKey?.childForeignKey,
            parentKey: matchKey?.parentKey,
            aiInstructions: childConfig?.aiInstructions || ''
          },
          grouping: {
            groupBy: 'table_row',
            extractAsGroup: true
          }
        }));
        
        fieldDefinitions = [...parentFields, ...childFields];
      } else if (isTableViewMode) {
        fieldDefinitions = columns.map(col => ({
          name: col.name,
          displayName: col.displayName || col.label || col.name,
          type: col.type || 'text',
          format: col.format,
          description: col.desc || '',
          required: col.required || false,
          cardinality: 'single',
          extractionMode: 'single_value',
          unit: col.unit,
          tableContext: {
            tableName: sectionConfig?.tableName || 'table',
            mode: 'table_view',
            aiInstructions: sectionConfig?.aiInstructions || ''
          }
        }));
      }
      
      // Process the extracted data to handle table structures properly with fuzzy matching
      const processedData = processTableDataExtraction(result.extractedData || {}, fieldDefinitions);
      
      // Store debug information
      const debugData = {
        timestamp: new Date().toISOString(),
        method: 'PDF Page Analysis',
        fileName: droppedFile.name,
        pageNumber: pageNumber,
        inputMessage: 'PDF page analysis request',
        fieldDefinitions: fieldDefinitions,
        rawResponse: result,
        extractedData: processedData,
        extractionNotes: result.extractionNotes,
        confidence: result.confidence,
        metadata: result.metadata,
        fieldCount: Object.keys(result.extractedData || {}).length,
        fuzzyMatches: processedData._fuzzyMatches || [],
        processingTime: Date.now(),
        tokenUsage: result.tokenUsage || null
      };
      
      updateDebugInfo(debugData);
      
      // Determine extraction type based on file type
      let extractionType = 'pdfOpenAI';
      
      if (onDataExtracted) {
        onDataExtracted({
          type: extractionType,
          data: processedData,
          confidence: result.confidence,
          extractionNotes: result.extractionNotes,
          metadata: result.metadata,
          pageNumber: pageNumber
        });
      }
      
      setIsProcessingOpenAI(false);
      // Don't call onFileProcessed() here - let user perform more extractions if needed
      
    } catch (error) {
      console.error('PDF page extraction failed:', error);
      setIsProcessingOpenAI(false);
      throw error;
    }
  };

  // Handle AI Image extraction with specific sheet
  const handleAIImageExtractorWithSheet = async (sheetName) => {
    console.log('🖼️ Starting AI Image extraction with specific sheet:', sheetName);
    
    try {
      setIsProcessingOpenAI(true);
      
      let result;
      
      // Check if this is an Excel file that needs to be converted to image first
      if (droppedFile.name.match(/\.(xlsx|xls)$/i)) {
        console.log('🖼️ Processing Excel file for image extraction:', droppedFile.name);
        console.log('📋 Using selected sheet:', sheetName);
        
        // Generate field definitions for the current form mode
        let fieldDefinitions = [];
        
        if (isSingleTableMode) {
          fieldDefinitions = columns.map(col => ({
            name: col.name,
            displayName: col.displayName || col.label || col.name,
            type: col.type || 'text',
            format: col.format,
            description: col.desc || '',
            required: col.required || false,
            cardinality: 'single',
            extractionMode: 'single_value',
            unit: col.unit,
            tableContext: {
              tableName: tableName,
              mode: 'single_table',
              aiInstructions: tableConfig?.aiInstructions || ''
            }
          }));
        } else if (isDualTableMode) {
          // Handle dual table mode
          fieldDefinitions = [
            ...(parentConfig?.columns || []).map(col => ({
              name: col.name,
              displayName: col.displayName || col.label || col.name,
              type: col.type || 'text',
              format: col.format,
              description: col.desc || '',
              required: col.required || false,
              cardinality: 'single',
              extractionMode: 'single_value',
              unit: col.unit,
              tableContext: {
                tableName: parentConfig?.tableName || 'parent',
                mode: 'dual_table_parent',
                relationship: 'parent',
                aiInstructions: parentConfig?.aiInstructions || ''
              }
            })),
            ...(childConfig?.columns || []).map(col => ({
              name: col.name,
              displayName: col.displayName || col.label || col.name,
              type: col.type || 'text',
              format: col.format,
              description: col.desc || '',
              required: col.required || false,
              cardinality: col.cardinality || 'multiple',
              extractionMode: col.cardinality === 'multiple' ? 'multiple_values' : 'single_value',
              unit: col.unit,
              grouping: col.cardinality === 'multiple' ? {
                extractAsGroup: true,
                groupBy: 'table_row'
              } : undefined,
              tableContext: {
                tableName: childConfig?.tableName || 'child',
                mode: 'dual_table_child',
                relationship: 'child',
                aiInstructions: childConfig?.aiInstructions || ''
              }
            }))
          ];
        } else if (isTableViewMode) {
          fieldDefinitions = columns.map(col => ({
            name: col.name,
            displayName: col.displayName || col.label || col.name,
            type: col.type || 'text',
            format: col.format,
            description: col.desc || '',
            required: col.required || false,
            cardinality: 'single',
            extractionMode: 'single_value',
            unit: col.unit,
            tableContext: {
              tableName: sectionConfig?.tableName || 'table',
              mode: 'table_view',
              aiInstructions: sectionConfig?.aiInstructions || ''
            }
          }));
        }
        
        // Call Excel-to-image endpoint with the specific sheet
        const formData = new FormData();
        formData.append('excel', droppedFile);
        formData.append('fieldDefinitions', JSON.stringify(fieldDefinitions));
        formData.append('selectedSheet', sheetName);
        
        const response = await fetch('/api/openai/excel-to-image', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json();
              errorMessage = errorData.error || errorData.details || errorMessage;
            }
          } catch (parseError) {
            console.error('Error parsing error response:', parseError);
          }
          throw new Error(errorMessage);
        }
        
        result = await response.json();
        console.log('✅ Excel-to-image conversion and analysis completed:', {
          confidence: result.confidence,
          extractedFields: Object.keys(result.extractedData || {}).length,
          sheetName: sheetName
        });
      } else {
        // Handle non-Excel files with regular image extraction
        result = await handleImageExtraction(droppedFile);
      }
      
      // For Excel files, the data is already processed by the backend
      const processedData = droppedFile.name.match(/\.(xlsx|xls)$/i) 
        ? result.extractedData || {}
        : processTableDataExtraction(result.extractedData || {}, []);
      
      // Store debug information
      const debugData = {
        timestamp: new Date().toISOString(),
        method: 'Excel Image Analysis',
        fileName: droppedFile.name,
        selectedSheet: sheetName,
        inputMessage: 'Excel image analysis request',
        rawResponse: result,
        extractedData: processedData,
        extractionNotes: result.extractionNotes,
        confidence: result.confidence,
        metadata: result.metadata,
        fieldCount: Object.keys(result.extractedData || {}).length,
        processingTime: Date.now(),
        tokenUsage: result.tokenUsage || null
      };
      
      updateDebugInfo(debugData);
      
      // Determine extraction type
      let extractionType = 'excelImageOpenAI';
      
      if (onDataExtracted) {
        onDataExtracted({
          type: extractionType,
          data: processedData,
          confidence: result.confidence,
          extractionNotes: result.extractionNotes,
          metadata: result.metadata,
          selectedSheet: sheetName
        });
      }
      
      setIsProcessingOpenAI(false);
      
      // Close the file options modal after successful extraction
      setShowFileOptions(false);
      
      // Process the file to reset the dropped file state
      onFileProcessed();
      
    } catch (error) {
      console.error('Excel Image extraction failed:', error);
      setIsProcessingOpenAI(false);
      throw error;
    }
  };

  // Handle AI Image extraction for non-PDF files
  const handleAIImageExtractorGeneric = async () => {
    try {
      setIsProcessingOpenAI(true);
      
      let result;
      
      // Check if this is an Excel file that needs to be converted to image first
      if (droppedFile.name.match(/\.(xlsx|xls)$/i)) {
        console.log('🖼️ Processing Excel file for image extraction:', droppedFile.name);
        console.log('📋 Using selected sheet:', selectedExcelSheet);
        
        // Generate field definitions for the current form mode
        let fieldDefinitions = [];
        
        if (isSingleTableMode) {
          fieldDefinitions = columns.map(col => ({
            name: col.name,
            displayName: col.displayName || col.label || col.name,
            type: col.type || 'text',
            format: col.format,
            description: col.desc || '',
            required: col.required || false,
            cardinality: 'single',
            extractionMode: 'single_value',
            unit: col.unit,
            tableContext: {
              tableName: tableName,
              mode: 'single_table',
              aiInstructions: tableConfig?.aiInstructions || ''
            }
          }));
        } else if (isDualTableMode) {
          // Handle dual table mode
          fieldDefinitions = [
            ...(parentConfig?.columns || []).map(col => ({
              name: col.name,
              displayName: col.displayName || col.label || col.name,
              type: col.type || 'text',
              format: col.format,
              description: col.desc || '',
              required: col.required || false,
              cardinality: 'single',
              extractionMode: 'single_value',
              unit: col.unit,
              tableContext: {
                tableName: parentConfig?.tableName || 'parent',
                mode: 'dual_table_parent',
                relationship: 'parent',
                aiInstructions: parentConfig?.aiInstructions || ''
              }
            })),
            ...(childConfig?.columns || []).map(col => ({
              name: col.name,
              displayName: col.displayName || col.label || col.name,
              type: col.type || 'text',
              format: col.format,
              description: col.desc || '',
              required: col.required || false,
              cardinality: col.cardinality || 'multiple',
              extractionMode: col.cardinality === 'multiple' ? 'multiple_values' : 'single_value',
              unit: col.unit,
              grouping: col.cardinality === 'multiple' ? {
                extractAsGroup: true,
                groupBy: 'table_row'
              } : undefined,
              tableContext: {
                tableName: childConfig?.tableName || 'child',
                mode: 'dual_table_child',
                relationship: 'child',
                aiInstructions: childConfig?.aiInstructions || ''
              }
            }))
          ];
        } else if (isTableViewMode) {
          fieldDefinitions = columns.map(col => ({
            name: col.name,
            displayName: col.displayName || col.label || col.name,
            type: col.type || 'text',
            format: col.format,
            description: col.desc || '',
            required: col.required || false,
            cardinality: 'single',
            extractionMode: 'single_value',
            unit: col.unit,
            tableContext: {
              tableName: sectionConfig?.tableName || 'table',
              mode: 'table_view',
              aiInstructions: sectionConfig?.aiInstructions || ''
            }
          }));
        }
        
        // Call Excel-to-image endpoint
        const formData = new FormData();
        formData.append('excel', droppedFile);
        formData.append('fieldDefinitions', JSON.stringify(fieldDefinitions));
        if (selectedExcelSheet) {
          formData.append('selectedSheet', selectedExcelSheet);
        }
        
        const response = await fetch('/api/openai/excel-to-image', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const errorData = await response.json();
              errorMessage = errorData.error || errorData.details || errorMessage;
            }
          } catch (parseError) {
            console.error('Error parsing error response:', parseError);
          }
          throw new Error(errorMessage);
        }
        
        result = await response.json();
        console.log('✅ Excel-to-image conversion and analysis completed:', {
          confidence: result.confidence,
          extractedFields: Object.keys(result.extractedData || {}).length,
          sheetName: selectedExcelSheet
        });
      } else {
        // Handle non-Excel files with regular image extraction
        result = await handleImageExtraction(droppedFile);
      }
      
      // Generate field definitions for the current form mode (for non-Excel files only)
      let fieldDefinitions = [];
      
      if (!droppedFile.name.match(/\.(xlsx|xls)$/i)) {
        // Only generate field definitions for non-Excel files (Excel already processed above)
        if (isSingleTableMode) {
          fieldDefinitions = columns.map(col => ({
            name: col.name,
            displayName: col.displayName || col.label || col.name,
            type: col.type || 'text',
            format: col.format,
            description: col.desc || '',
            required: col.required || false,
            cardinality: 'single',
            extractionMode: 'single_value',
            unit: col.unit,
            tableContext: {
              tableName: tableName,
              mode: 'single_table',
              aiInstructions: tableConfig?.aiInstructions || ''
            }
          }));
        } else if (isDualTableMode) {
          const parentFields = (parentConfig?.columns || []).map(col => ({
            name: col.name,
            displayName: col.displayName || col.label || col.name,
            type: col.type || 'text',
            format: col.format,
            description: col.desc || '',
            required: col.required || false,
            cardinality: 'single',
            extractionMode: 'single_value',
            unit: col.unit,
            tableType: 'parent',
            tableContext: {
              tableName: tables?.parent || 'parent',
              mode: 'dual_table_parent',
              relationship: 'one_to_many_parent',
              aiInstructions: parentConfig?.aiInstructions || ''
            }
          }));
          
          const childFields = (childConfig?.columns || []).map(col => ({
            name: col.name,
            displayName: col.displayName || col.label || col.name,
            type: col.type || 'text',
            format: col.format,
            description: col.desc || '',
            required: col.required || false,
            cardinality: 'multiple',
            extractionMode: 'table_rows',
            unit: col.unit,
            tableType: 'child',
            tableContext: {
              tableName: tables?.child || 'child',
              mode: 'dual_table_child',
              relationship: 'one_to_many_child',
              parentTable: tables?.parent || 'parent',
              foreignKey: matchKey?.childForeignKey,
              parentKey: matchKey?.parentKey,
              aiInstructions: childConfig?.aiInstructions || ''
            },
            grouping: {
              groupBy: 'table_row',
              extractAsGroup: true
            }
          }));
          
          fieldDefinitions = [...parentFields, ...childFields];
        } else if (isTableViewMode) {
          fieldDefinitions = columns.map(col => ({
            name: col.name,
            displayName: col.displayName || col.label || col.name,
            type: col.type || 'text',
            format: col.format,
            description: col.desc || '',
            required: col.required || false,
            cardinality: 'single',
            extractionMode: 'single_value',
            unit: col.unit,
            tableContext: {
              tableName: sectionConfig?.tableName || 'table',
              mode: 'table_view',
              aiInstructions: sectionConfig?.aiInstructions || ''
            }
          }));
        }
      }
      
      // Process the extracted data to handle table structures properly with fuzzy matching
      // For Excel files, the data is already processed by the backend
      const processedData = droppedFile.name.match(/\.(xlsx|xls)$/i) 
        ? result.extractedData || {}
        : processTableDataExtraction(result.extractedData || {}, fieldDefinitions);
      
      // Store debug information
      const debugData = {
        timestamp: new Date().toISOString(),
        method: 'Image Analysis',
        fileName: droppedFile.name,
        inputMessage: 'Image analysis request',
        fieldDefinitions: fieldDefinitions,
        rawResponse: result,
        extractedData: processedData,
        extractionNotes: result.extractionNotes,
        confidence: result.confidence,
        metadata: result.metadata,
        fieldCount: Object.keys(result.extractedData || {}).length,
        fuzzyMatches: processedData._fuzzyMatches || [],
        processingTime: Date.now(),
        tokenUsage: result.tokenUsage || null
      };
      
      updateDebugInfo(debugData);
      
      // Determine extraction type based on file type
      let extractionType = 'imageOpenAI';
      if (droppedFile.name.match(/\.(pdf)$/i)) {
        extractionType = 'pdfOpenAI';
      } else if (droppedFile.name.match(/\.(xlsx|xls)$/i)) {
        extractionType = 'excelImageOpenAI';
      }
      
      if (onDataExtracted) {
        onDataExtracted({
          type: extractionType,
          data: processedData,
          confidence: result.confidence,
          extractionNotes: result.extractionNotes,
          metadata: result.metadata
        });
      }
      
      setIsProcessingOpenAI(false);
      
      // Close the file options modal after successful extraction
      setShowFileOptions(false);
      
      // Process the file to reset the dropped file state
      onFileProcessed();
      
    } catch (error) {
      console.error('Image extraction failed:', error);
      setIsProcessingOpenAI(false);
      throw error;
    }
  };

  // Utility function to optimize field definitions and remove duplication
  const optimizeFieldDefinitions = (fieldDefinitions) => {
    // Group fields by extraction mode and cardinality to reduce redundancy
    const groupedFields = {
      single: [],
      multiple: []
    };
    
    const aiInstructionsSet = new Set();
    
    fieldDefinitions.forEach(field => {
      // Extract AI instructions once
      if (field.tableContext?.aiInstructions) {
        aiInstructionsSet.add(field.tableContext.aiInstructions);
      }
      
      // Create minimal field definition
      const optimizedField = {
        name: field.name,
        displayName: field.displayName || field.label || field.name,
        type: field.type || 'text',
        ...(field.format && { format: field.format }),
        ...(field.description && { description: field.description }),
        ...(field.required && { required: field.required }),
        cardinality: field.cardinality || 'single',
        extractionMode: field.extractionMode || 'single_value',
        ...(field.unit && { unit: field.unit })
      };
      
      // Add table context only if it contains unique information
      if (field.tableContext) {
        optimizedField.tableContext = {
          tableName: field.tableContext.tableName,
          mode: field.tableContext.mode,
          ...(field.tableContext.relationship && { relationship: field.tableContext.relationship })
        };
      }
      
      // Add grouping info only for grouped fields
      if (field.grouping?.extractAsGroup) {
        optimizedField.grouping = {
          extractAsGroup: true,
          groupBy: field.grouping.groupBy || 'table_row'
        };
      }
      
      // Group by cardinality
      if (field.cardinality === 'multiple') {
        groupedFields.multiple.push(optimizedField);
      } else {
        groupedFields.single.push(optimizedField);
      }
    });
    
    return {
      fieldGroups: groupedFields,
      aiInstructions: Array.from(aiInstructionsSet).join('\n\n'),
      totalFields: fieldDefinitions.length
    };
  };

  // Enhanced function to process table data extraction results with fuzzy matching
  const processTableDataExtraction = (extractedData, fieldDefinitions) => {
    console.log('📊 Processing table data extraction with fuzzy matching:', extractedData);
    
    const processedData = {};
    const fuzzyMatches = [];
    
    // Find fields that should be grouped as table rows
    const groupedFields = fieldDefinitions.filter(f => f.grouping?.extractAsGroup);
    const singleFields = fieldDefinitions.filter(f => f.cardinality === 'single');
    
    // Helper function to recursively search for values in nested objects
    const deepSearch = (obj, targetField, path = '') => {
      const results = [];
      
      if (obj === null || obj === undefined) return results;
      
      if (typeof obj === 'object' && !Array.isArray(obj)) {
        // Search in object keys and values
        for (const [key, value] of Object.entries(obj)) {
          const currentPath = path ? `${path}.${key}` : key;
          
          // Check if this key matches our target field
          const similarity = calculateSimilarity(key.toLowerCase(), targetField.toLowerCase());
          if (similarity >= 0.8) {
            results.push({
              value: value,
              path: currentPath,
              similarity: similarity,
              source: 'key'
            });
          }
          
          // Recursively search in nested objects
          if (typeof value === 'object' && value !== null) {
            results.push(...deepSearch(value, targetField, currentPath));
          }
        }
      } else if (Array.isArray(obj)) {
        // Search in array elements
        obj.forEach((item, index) => {
          const currentPath = `${path}[${index}]`;
          results.push(...deepSearch(item, targetField, currentPath));
        });
      }
      
      return results;
    };
    
    // Helper function to find best fuzzy match for a field
    const findFuzzyMatch = (field, extractedData) => {
      const fieldName = field.name.toLowerCase();
      const fieldDisplayName = (field.displayName || field.label || field.name).toLowerCase();
      
      // First, try exact matches
      if (extractedData[field.name] !== undefined) {
        return { value: extractedData[field.name], similarity: 1.0, path: field.name };
      }
      
      // Then try fuzzy matching on top-level keys
      let bestMatch = null;
      let bestSimilarity = 0;
      
      for (const [key, value] of Object.entries(extractedData)) {
        const keySimilarity = calculateSimilarity(key.toLowerCase(), fieldName);
        const displayNameSimilarity = calculateSimilarity(key.toLowerCase(), fieldDisplayName);
        const similarity = Math.max(keySimilarity, displayNameSimilarity);
        
        if (similarity > bestSimilarity && similarity >= 0.8) {
          bestSimilarity = similarity;
          bestMatch = { value: value, similarity: similarity, path: key };
        }
      }
      
      // If no good match found at top level, search deeper
      if (!bestMatch || bestSimilarity < 0.8) {
        const deepResults = deepSearch(extractedData, fieldName);
        if (deepResults.length > 0) {
          // Sort by similarity and take the best match
          deepResults.sort((a, b) => b.similarity - a.similarity);
          const bestDeepMatch = deepResults[0];
          
          if (bestDeepMatch.similarity >= 0.8) {
            bestMatch = bestDeepMatch;
          }
        }
      }
      
      return bestMatch;
    };
    
    // Process single value fields with fuzzy matching
    singleFields.forEach(field => {
      const match = findFuzzyMatch(field, extractedData);
      
      if (match) {
        processedData[field.name] = match.value;
        
        if (match.similarity < 1.0) {
          fuzzyMatches.push({
            field: field.name,
            originalPath: match.path,
            similarity: match.similarity,
            value: match.value
          });
        }
      }
    });
    
    // Process grouped table data
    if (groupedFields.length > 0) {
      const tableFieldNames = groupedFields.map(f => f.name);
      
      // Look for table data in the extracted results
      const tableData = [];
      
      // Check if we have array data that should be grouped
      const arrayFields = Object.keys(extractedData).filter(key => 
        Array.isArray(extractedData[key]) && tableFieldNames.includes(key)
      );
      
      if (arrayFields.length > 0) {
        // Process array data as table rows
        const firstArrayField = arrayFields[0];
        const arrayData = extractedData[firstArrayField];
        
        if (Array.isArray(arrayData) && arrayData.length > 0) {
          // Check if array contains objects (proper table rows)
          if (typeof arrayData[0] === 'object' && arrayData[0] !== null) {
            // Data is already in table format
            tableData.push(...arrayData);
          } else {
            // Convert simple array to table format
            arrayData.forEach((value, index) => {
              const row = {};
              tableFieldNames.forEach(fieldName => {
                if (fieldName === firstArrayField) {
                  row[fieldName] = value;
                } else if (extractedData[fieldName] && Array.isArray(extractedData[fieldName])) {
                  row[fieldName] = extractedData[fieldName][index] || '';
                }
              });
              tableData.push(row);
            });
          }
        }
      }
      
      // If we have table data, add it to processed data
      if (tableData.length > 0) {
        // For dual table mode, separate parent and child data
        if (isDualTableMode) {
          const parentFields = groupedFields.filter(f => f.tableType === 'parent');
          const childFields = groupedFields.filter(f => f.tableType === 'child');
          
          if (parentFields.length > 0 && tableData.length > 0) {
            // Extract parent data from first row
            const parentData = {};
            parentFields.forEach(field => {
              if (tableData[0][field.name] !== undefined) {
                parentData[field.name] = tableData[0][field.name];
              }
            });
            Object.assign(processedData, parentData);
          }
          
          if (childFields.length > 0) {
            // Extract child data as array
            const childData = tableData.map(row => {
              const childRow = {};
              childFields.forEach(field => {
                if (row[field.name] !== undefined) {
                  childRow[field.name] = row[field.name];
                }
              });
              return childRow;
            });
            
            // Add child data using the first child field name as key
            if (childFields.length > 0) {
              processedData[childFields[0].name] = childData;
            }
          }
        } else {
          // For single table or table view mode, return table data directly
          processedData.tableData = tableData;
        }
      }
    }
    
    // Log fuzzy matches for debugging
    if (fuzzyMatches.length > 0) {
      console.log('🔍 Fuzzy matches found:', fuzzyMatches);
      processedData._fuzzyMatches = fuzzyMatches;
    }
    
    console.log('✅ Processed table data with fuzzy matching:', processedData);
    return processedData;
  };

  // Handle Excel mapping button click
  const handleExcelMappingClick = async () => {
    console.log('🔥 DEBUG: handleExcelMappingClick started');
    console.log('🔥 DEBUG: droppedFile:', droppedFile?.name, droppedFile?.size);
    
    if (!droppedFile) {
      console.log('❌ DEBUG: No dropped file found');
      return;
    }
    
    console.log('🔥 DEBUG: About to call prepareFileForExtraction');
    try {
      const shouldProceed = await prepareFileForExtraction(droppedFile, 'mapping');
      console.log('🔥 DEBUG: prepareFileForExtraction returned:', shouldProceed);
      
      if (!shouldProceed) {
        console.log('🔥 DEBUG: prepareFileForExtraction returned false, not proceeding');
        return;
      }
      
      console.log('🔥 DEBUG: Setting showExcelMapping to true');
      setShowExcelMapping(true);
      console.log('🔥 DEBUG: handleExcelMappingClick completed successfully');
    } catch (error) {
      console.error('❌ DEBUG: Error in handleExcelMappingClick:', error);
    }
  };

  // Handle Smart Excel extractor button click
  const handleSmartExcelClick = async () => {
    console.log('🔥 DEBUG: handleSmartExcelClick started');
    console.log('🔥 DEBUG: droppedFile:', droppedFile?.name, droppedFile?.size);
    
    if (!droppedFile) {
      console.log('❌ DEBUG: No dropped file found');
      return;
    }
    
    console.log('🔥 DEBUG: About to call prepareFileForExtraction');
    try {
      const shouldProceed = await prepareFileForExtraction(droppedFile, 'smart');
      console.log('🔥 DEBUG: prepareFileForExtraction returned:', shouldProceed);
      
      if (!shouldProceed) {
        console.log('🔥 DEBUG: prepareFileForExtraction returned false, not proceeding');
        return;
      }
      
      console.log('🔥 DEBUG: Setting showSmartExcelExtractor to true');
      setShowSmartExcelExtractor(true);
      console.log('🔥 DEBUG: handleSmartExcelClick completed successfully');
    } catch (error) {
      console.error('❌ DEBUG: Error in handleSmartExcelClick:', error);
    }
  };

  return (
    <>
      <style>
        {`
        .file-options-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          cursor: ${isProcessingOpenAI ? 'wait' : 'default'};
        }
        
        .file-options-modal {
          background: white;
          border-radius: 12px;
          padding: 0;
          width: 90%;
          max-width: 600px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          cursor: ${isProcessingOpenAI ? 'wait' : 'default'};
        }
        
        .file-options-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e0e0e0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        
        .file-options-header h4 {
          margin: 0;
          font-size: 1.2rem;
          font-weight: 600;
        }
        
        .btn-close {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: ${isProcessingOpenAI ? 'wait' : 'pointer'};
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        
        .btn-close:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: ${isProcessingOpenAI ? 'none' : 'scale(1.1)'};
        }
        
        .btn-close:disabled {
          opacity: 0.5;
          cursor: wait;
        }
        
        .file-options-content {
          padding: 24px;
        }
        
        .file-options-content p {
          margin: 0 0 20px 0;
          color: #666;
          font-size: 1rem;
        }
        
        .file-options-buttons {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .file-options-buttons .btn {
          padding: 12px 20px;
          border: none;
          border-radius: 8px;
          cursor: ${isProcessingOpenAI ? 'wait' : 'pointer'};
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.2s ease;
          text-align: center;
          position: relative;
        }
        
        .file-options-buttons .btn:hover {
          transform: ${isProcessingOpenAI ? 'none' : 'translateY(-2px)'};
          box-shadow: ${isProcessingOpenAI ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.15)'};
        }
        
        .file-options-buttons .btn:disabled {
          opacity: 0.6;
          cursor: wait;
        }
        
        .btn-primary {
          background: #007bff;
          color: white;
        }
        
        .btn-primary:hover {
          background: ${isProcessingOpenAI ? '#007bff' : '#0056b3'};
        }
        
        .btn-success {
          background: #28a745;
          color: white;
        }
        
        .btn-success:hover {
          background: ${isProcessingOpenAI ? '#28a745' : '#1e7e34'};
        }
        
        .btn-secondary {
          background: #6c757d;
          color: white;
        }
        
        .btn-secondary:hover {
          background: ${isProcessingOpenAI ? '#6c757d' : '#545b62'};
        }
        
        .btn-danger {
          background: #dc3545;
          color: white;
        }
        
        .btn-danger:hover {
          background: ${isProcessingOpenAI ? '#dc3545' : '#c82333'};
        }
        
        .btn-warning {
          background: #ffc107;
          color: #212529;
        }
        
        .btn-warning:hover {
          background: ${isProcessingOpenAI ? '#ffc107' : '#e0a800'};
        }

        /* Excel Sheet Selector Modal Styles */
        .excel-sheet-selector-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100000; /* Higher than other modals */
          cursor: ${isProcessingOpenAI ? 'wait' : 'default'};
        }
        
        .excel-sheet-selector-modal {
          background: white;
          border-radius: 12px;
          padding: 0;
          width: 90%;
          max-width: 600px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          cursor: ${isProcessingOpenAI ? 'wait' : 'default'};
        }
        
        .excel-sheet-selector-content {
          padding: 0;
        }
        
        .openai-extractor-modal {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        
        .openai-extractor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid #e0e0e0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        
        .openai-extractor-header h3 {
          margin: 0;
          font-size: 1.2rem;
          font-weight: 600;
        }
        
        .openai-extractor-content {
          padding: 24px;
          flex: 1;
        }
        
        .openai-extractor-content p {
          margin: 0 0 20px 0;
          color: #666;
          font-size: 1rem;
        }
        
        .openai-extractor-content ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .openai-extractor-content li {
          margin: 12px 0;
        }
        
        .openai-extractor-content li button {
          width: 100%;
          padding: 12px 20px;
          border: none;
          border-radius: 8px;
          cursor: ${isProcessingOpenAI ? 'wait' : 'pointer'};
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.2s ease;
          text-align: center;
          background: #007bff;
          color: white;
        }
        
        .openai-extractor-content li button:hover {
          background: ${isProcessingOpenAI ? '#007bff' : '#0056b3'};
          transform: ${isProcessingOpenAI ? 'none' : 'translateY(-2px)'};
          box-shadow: ${isProcessingOpenAI ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.15)'};
        }
        
        .openai-extractor-content li button:disabled {
          opacity: 0.6;
          cursor: wait;
        }
        
        /* Processing indicator styles */
        .processing-indicator {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 255, 255, 0.9);
          padding: 20px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1001;
        }
        
        .processing-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        `}
      </style>
      
      {/* File Options Modal */}
      {showFileOptions && droppedFile && (
        <div className="file-options-overlay">
          <div className="file-options-modal">
            <div className="file-options-header">
              <h4>File Dropped: {droppedFile.name}</h4>
              <button 
                className="btn-close" 
                disabled={isProcessingOpenAI}
                onClick={() => {
                  console.log('🔥 DEBUG: File options modal close button clicked');
                  if (!isProcessingOpenAI) {
                    setShowFileOptions(false);
                    onFileProcessed();
                  }
                }}
              >
                ×
              </button>
            </div>
            <div className="file-options-content">
              <p>What would you like to do with this file?</p>
              <div className="file-options-buttons">
                {isSingleTableMode && (
                  <button 
                    type="button"
                    className="btn btn-primary"
                    disabled={isProcessingOpenAI}
                    onClick={handleSaveAsAttachment}
                  >
                    📎 Save as Attachment
                  </button>
                )}
                
                {(droppedFile?.name.match(/\.(xlsx|xls)$/i)) && (
                  <>
                    <button 
                      type="button"
                      className="btn btn-primary"
                      disabled={isProcessingOpenAI}
                      onClick={handleExcelMappingClick}
                    >
                      📊 Excel Mapping
                    </button>
                   
          
                    <button 
                      type="button"
                      className="btn btn-danger"
                      disabled={isProcessingOpenAI}
                      onClick={handleAIImageExtractor}
                    >
                      {isProcessingOpenAI ? '⏳ Processing...' : '🖼️ AI Extractor'}
                    </button>
                  </>
                )}
                
                {(droppedFile?.name.match(/\.(pdf)$/i)) && (
                  <button 
                    type="button"
                    className="btn btn-danger"
                    disabled={isProcessingOpenAI}
                    onClick={handleAIImageExtractor}
                  >
                    {isProcessingOpenAI ? '⏳ Processing...' : '📄 AI PDF Extractor (GPT-4o)'}
                  </button>
                )}
                
                {(droppedFile?.name.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)) && (
                  <button 
                    type="button"
                    className="btn btn-danger"
                    disabled={isProcessingOpenAI}
                    onClick={handleAIImageExtractor}
                  >
                    {isProcessingOpenAI ? '⏳ Processing...' : '🖼️ AI Image Extractor (GPT-4o)'}
                  </button>
                )}
                
                <button 
                  type="button"
                  className="btn btn-secondary"
                  disabled={isProcessingOpenAI}
                  onClick={handleReadToForm}
                >
                  {isProcessingFile ? '⏳ Processing...' : '📊 Read to Form'}
                </button>
              </div>
              
              {/* Processing Indicator */}
              {isProcessingOpenAI && (
                <div className="processing-indicator">
                  <div className="processing-spinner"></div>
                  <span>Processing with AI...</span>
                </div>
              )}
              
              <div className="file-options-info">
                <small>
                  {isSingleTableMode && (
                    <>
                      <strong>Save as Attachment:</strong> Store file with record metadata<br/>
                    </>
                  )}
                  {(droppedFile?.name.match(/\.(xlsx|xls)$/i)) && (
                    <>
                      <strong>Excel Mapping:</strong> Advanced Excel extraction with fuzzy matching<br/>
                      <strong>Smart Excel Extractor:</strong> AI-powered client-side extraction with real-time processing<br/>
                      <strong>AI Excel Extractor:</strong> OpenAI-powered extraction with intelligent field mapping and data location detection<br/>
                      <strong>AI Image Extractor:</strong> Uses <b>GPT-4o</b> to extract data from an image of the Excel sheet<br/>
                    </>
                  )}
                  {(droppedFile?.name.match(/\.(pdf)$/i)) && (
                    <>
                      <strong>AI PDF Extractor:</strong> Converts PDF to image and uses <b>GPT-4o Vision</b> to extract form data. Automatically falls back to text extraction if image conversion fails.<br/>
                    </>
                  )}
                  {(droppedFile?.name.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)) && (
                    <>
                      <strong>AI Image Extractor:</strong> Uses <b>GPT-4o Vision</b> to extract form data from images<br/>
                    </>
                  )}
                  <strong>Read to Form:</strong> Extract data from various file types
                  <br/>
                  <em>Supports: Excel, Word, PDF, CSV, TXT, Images (OCR)</em>
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Form Modal */}
      {isSingleTableMode && showAttachmentForm && droppedFile && (
        <DataFormAttachment
          file={droppedFile}
          tableId={formData[Object.keys(formData).find(key => 
            key.toLowerCase().includes('id') && formData[key]
          ) || Object.keys(formData)[0]]}
          tableName={tableName}
          onSave={async (attachmentData) => {
            console.log('Saving attachment:', attachmentData);
            if (onAttachmentSaved) {
              onAttachmentSaved(attachmentData);
            }
            setShowAttachmentForm(false);
            onFileProcessed();
          }}
          onCancel={() => {
            setShowAttachmentForm(false);
            onFileProcessed();
          }}
        />
      )}

      {/* Excel Mapping Modal */}
      {showExcelMapping && droppedFile && (
        <div className="data-mapping-excel-overlay">
          <div className="data-mapping-excel">
            <div className="excel-content">
              <DataMappingExcel
                onDataExtracted={handleExcelDataExtracted}
                onCancel={() => { 
                  setShowExcelMapping(false); 
                  onFileProcessed(); 
                }}
                targetLabels={
                  isSingleTableMode
                    ? columns.map(col => col.displayName || col.label || col.name)
                    : isDualTableMode
                      ? [
                          ...(parentConfig?.columns || []).map(col => col.displayName || col.label || col.name),
                          ...(childConfig?.columns || []).map(col => col.displayName || col.label || col.name)
                        ]
                      : []
                }
                parentLabels={
                  isSingleTableMode
                    ? columns.map(col => col.displayName || col.label || col.name)
                    : isDualTableMode
                      ? (parentConfig?.columns || []).map(col => col.displayName || col.label || col.name)
                      : []
                }
                childLabels={
                  isDualTableMode
                    ? (childConfig?.columns || []).map(col => col.displayName || col.label || col.name)
                    : []
                }
                title="Excel Data Extraction"
                subtitle="Extract data from Excel files with advanced pattern matching"
                initialFile={droppedFile}
              />
            </div>
          </div>
        </div>
      )}

      {/* Smart Excel Extractor Modal */}
      {showSmartExcelExtractor && droppedFile && (
        <div className="data-mapping-excel-overlay">
          <div className="data-mapping-excel">
            <div className="excel-content">
              <SmartExcelExtractor
                onDataExtracted={handleSmartExcelDataExtracted}
                onCancel={() => { 
                  setShowSmartExcelExtractor(false); 
                  onFileProcessed(); 
                }}
                targetLabels={
                  isSingleTableMode
                    ? columns.map(col => col.displayName || col.label || col.name)
                    : isDualTableMode
                      ? [
                          ...(parentConfig?.columns || []).map(col => col.displayName || col.label || col.name),
                          ...(childConfig?.columns || []).map(col => col.displayName || col.label || col.name)
                        ]
                      : isTableViewMode
                        ? columns.map(col => col.displayName || col.label || col.name)
                        : []
                }
                parentConfig={parentConfig}
                childConfig={childConfig}
                isDualTableMode={isDualTableMode}
                title="Smart Excel Extractor"
                subtitle="AI-powered Excel data extraction with intelligent field matching"
                initialFile={droppedFile}
                mode="auto"
              />
            </div>
          </div>
        </div>
      )}

      {/* OpenAI Excel Extractor Modal */}
      {showOpenAIExtractor && openAIResults && (
        <div className="data-mapping-excel-overlay">
          <div className="data-mapping-excel">
            <div className="excel-content">
              <div className="openai-extractor-modal">
                <div className="openai-extractor-header">
                  <h3>🤖 AI Excel Extractor Results</h3>
                  <button 
                    className="btn-close" 
                    onClick={() => {
                      setShowOpenAIExtractor(false);
                      setOpenAIResults(null);
                      onFileProcessed();
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <div className="openai-extractor-content">
                  <div className="extraction-summary">
                    <h4>📊 Extraction Summary</h4>
                    <div className="summary-stats">
                      <div className="stat">
                        <span className="stat-label">Confidence:</span>
                        <span className="stat-value">{(openAIResults.confidence * 100).toFixed(1)}%</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Fields Found:</span>
                        <span className="stat-value">{Object.keys(openAIResults.extractedData).length}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="extracted-data-preview">
                    <h4>📋 Extracted Data Preview</h4>
                    <div className="data-preview-grid">
                      {Object.entries(openAIResults.extractedData).map(([fieldName, value]) => (
                        <div key={fieldName} className="data-preview-item">
                          <div className="field-name">{fieldName}</div>
                          <div className="field-value">
                            {Array.isArray(value) ? (
                              <div className="array-value">
                                {value.slice(0, 3).map((item, idx) => (
                                  <span key={idx} className="array-item">{String(item)}</span>
                                ))}
                                {value.length > 3 && <span className="array-more">+{value.length - 3} more</span>}
                              </div>
                            ) : (
                              <span className="single-value">{String(value)}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {openAIResults.recommendations && openAIResults.recommendations.length > 0 && (
                    <div className="ai-recommendations">
                      <h4>💡 AI Recommendations</h4>
                      <ul>
                        {openAIResults.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  

                  
                  {/* Debug Button in Extractor Modal */}
                  {debugInfo && (
                    <div className="debug-section-inline">
                      <h4>🔍 Debug Information Available</h4>
                      <p>Review the AI extraction process details including input prompts, raw responses, and processing metadata.</p>
                      <button 
                        className="btn btn-debug"
                        onClick={() => setShowDebugPanel(true)}
                        style={{ 
                          background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          border: 'none',
                          marginBottom: '16px'
                        }}
                      >
                        🔍 Open Debug Panel
                      </button>
                    </div>
                  )}

                  <div className="extractor-actions">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowOpenAIExtractor(false);
                        setOpenAIResults(null);
                        onFileProcessed();
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleOpenAIExtractionResults(true)}
                      style={{ 
                        background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                        border: 'none'
                      }}
                    >
                      ✅ Apply Data to Form
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sheet Selection Modal */}
      {showOpenAISheetSelector && openAISheetNames.length > 0 && (
        <div className="data-mapping-excel-overlay">
          <div className="data-mapping-excel">
            <div className="excel-content">
              <div className="openai-extractor-modal">
                <div className="openai-extractor-header">
                  <h3>🤖 Select Excel Sheet for AI Analysis</h3>
                  <button className="btn-close" onClick={() => setShowOpenAISheetSelector(false)}>×</button>
                </div>
                <div className="openai-extractor-content">
                  <p>Please select a sheet to analyze:</p>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {openAISheetNames.map(sheet => (
                      <li key={sheet} style={{ margin: '12px 0' }}>
                        <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => handleOpenAISheetSelect(sheet)}>{sheet}</button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Excel Sheet Selection Modal (for all Excel extraction methods) */}
      {showExcelSheetSelector && excelSheetNames.length > 0 && (
        <div className="excel-sheet-selector-overlay">
          <div className="excel-sheet-selector-modal">
            <div className="excel-sheet-selector-content">
              <div className="openai-extractor-modal">
                <div className="openai-extractor-header">
                  <h3>📊 Select Excel Sheet</h3>
                  <button 
                    className="btn-close" 
                    disabled={isProcessingOpenAI}
                    onClick={() => {
                      console.log('🔥 DEBUG: Sheet selector close button clicked');
                      if (!isProcessingOpenAI) {
                        setShowExcelSheetSelector(false);
                        setPendingExcelAction(null);
                      }
                    }}
                  >
                    ×
                  </button>
                </div>
                <div className="openai-extractor-content">
                  <p>This Excel file contains multiple sheets. Please select which sheet to process:</p>
                  {(() => {
                    console.log('🔥 DEBUG: Sheet selector modal is being rendered');
                    console.log('🔥 DEBUG: Available sheets:', excelSheetNames);
                    console.log('🔥 DEBUG: showExcelSheetSelector:', showExcelSheetSelector);
                    return null;
                  })()}
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {excelSheetNames.map(sheet => (
                      <li key={sheet} style={{ margin: '12px 0' }}>
                        <button 
                          type="button"
                          className="btn btn-primary" 
                          style={{ width: '100%' }} 
                          disabled={isProcessingOpenAI}
                          onClick={() => {
                            console.log('🔥 DEBUG: Sheet button clicked for:', sheet);
                            handleExcelSheetSelect(sheet);
                          }}
                        >
                          📋 {sheet}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button 
                      type="button"
                      className="btn btn-secondary"
                      disabled={isProcessingOpenAI}
                      onClick={() => {
                        console.log('🔥 DEBUG: Sheet selector cancel button clicked');
                        if (!isProcessingOpenAI) {
                          setShowExcelSheetSelector(false);
                          setPendingExcelAction(null);
                        }
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Page Selection Modal */}
      {showPDFPageSelector && pdfPageCount > 0 && (
        <div className="data-mapping-excel-overlay">
          <div className="data-mapping-excel">
            <div className="excel-content">
              <div className="openai-extractor-modal">
                <div className="openai-extractor-header">
                  <h3>📄 Select PDF Page</h3>
                  <button className="btn-close" onClick={() => setShowPDFPageSelector(false)}>×</button>
                </div>
                <div className="openai-extractor-content">
                  <p>This PDF file contains {pdfPageCount} page{pdfPageCount > 1 ? 's' : ''}. Please select which page to process:</p>
                  
                  {/* Simple page selector - in a full implementation, you'd show page previews */}
                  <div style={{ margin: '20px 0' }}>
                    <label htmlFor="page-selector" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                      Page Number:
                    </label>
                    <select 
                      id="page-selector"
                      value={selectedPDFPage}
                      onChange={(e) => setSelectedPDFPage(parseInt(e.target.value))}
                      style={{ 
                        width: '100%', 
                        padding: '10px', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                    >
                      {Array.from({ length: pdfPageCount }, (_, i) => i + 1).map(pageNum => (
                        <option key={pageNum} value={pageNum}>
                          Page {pageNum}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowPDFPageSelector(false)}
                      style={{ marginRight: '10px' }}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={() => handlePDFPageSelect(selectedPDFPage)}
                      style={{ 
                        background: 'linear-gradient(45deg, #f59e42 0%, #f53844 100%)',
                        border: 'none',
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    >
                      Process Page {selectedPDFPage}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Extraction Preview Modal */}
      {showOpenAIDataPreview && openAIPreviewData && (
        <div className="data-mapping-excel-overlay">
          <div className="data-mapping-excel">
            <div className="excel-content">
              <div className="openai-extractor-modal">
                <div className="openai-extractor-header">
                  <h3>📊 Excel Data Preview - {openAIPreviewData.sheetName}</h3>
                  <button className="btn-close" onClick={() => setShowOpenAIDataPreview(false)}>×</button>
                </div>
                
                <div className="openai-extractor-content">
                  <div className="data-preview-summary">
                    <div className="summary-stats">
                      <div className="stat-item">
                        <span className="stat-label">📋 Total Rows:</span>
                        <span className="stat-value">{openAIPreviewData.totalRows}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">📊 Total Columns:</span>
                        <span className="stat-value">{openAIPreviewData.totalColumns}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">🎯 Potential Matches:</span>
                        <span className="stat-value">
                          {openAIPreviewData.detectedColumns.filter(col => col.matchedField).length}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="detected-columns-section">
                    <h4>🔍 Detected Excel Columns & Potential Form Field Matches</h4>
                    <div className="columns-preview-grid">
                      {openAIPreviewData.detectedColumns.map((column, index) => (
                        <div key={index} className="column-preview-item">
                          <div className="excel-column-header">
                            <strong>📊 {column.excelColumn}</strong>
                            <span className="column-index">Column {String.fromCharCode(65 + column.columnIndex)}</span>
                          </div>
                          
                          <div className="sample-values">
                            <div className="sample-values-label">Sample Values:</div>
                            <div className="sample-values-list">
                              {column.sampleValues.length > 0 ? (
                                column.sampleValues.map((value, idx) => (
                                  <span key={idx} className="sample-value">{value}</span>
                                ))
                              ) : (
                                <span className="no-values">No data found</span>
                              )}
                            </div>
                            <div className="values-count">
                              {column.totalValues} values found
                            </div>
                          </div>

                          {column.matchedField ? (
                            <div className="matched-field">
                              <div className="match-indicator">
                                ✅ <strong>Potential Match:</strong>
                              </div>
                              <div className="match-details">
                                <div className="match-field-name">{column.matchedField.displayName}</div>
                                <div className="match-metadata">
                                  <span className="match-type">{column.matchedField.type}</span>
                                  {column.matchedField.tableType && column.matchedField.tableType !== 'single' && (
                                    <span className="match-table-type">({column.matchedField.tableType})</span>
                                  )}
                                  <span className="match-confidence">
                                    {(column.matchedField.confidence * 100).toFixed(0)}% match
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="no-match">
                              <div className="no-match-indicator">
                                ❓ <strong>No clear match found</strong>
                              </div>
                              <div className="no-match-note">
                                AI analysis may still find a suitable mapping
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="data-sample-section">
                    <h4>📋 Data Sample (First 5 Rows)</h4>
                    <div className="data-sample-table-wrapper">
                      <table className="data-sample-table">
                        <thead>
                          <tr>
                            {openAIPreviewData.dataPreview.headers.map((header, idx) => (
                              <th key={idx}>{header || `Column ${idx + 1}`}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {openAIPreviewData.dataPreview.sampleRows.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              {openAIPreviewData.dataPreview.headers.map((_, colIdx) => (
                                <td key={colIdx}>
                                  {row[colIdx] !== undefined ? String(row[colIdx]) : ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="preview-actions">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowOpenAIDataPreview(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={handleProceedWithOpenAIAnalysis}
                      disabled={isProcessingOpenAI}
                      style={{ 
                        background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                        border: 'none'
                      }}
                    >
                      {isProcessingOpenAI ? '🤖 Analyzing...' : '🚀 Proceed with AI Analysis'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Debug Panel */}
      {showDebugPanel && (
        <DebugPanel 
          debugInfo={debugInfo} 
          onClose={() => setShowDebugPanel(false)} 
        />
      )}
      
      {/* Debug Button - Shows after AI extraction completion */}
      {debugInfo && !showDebugPanel && (
        <div className="debug-button-container">
          <button 
            className="debug-review-button"
            onClick={() => setShowDebugPanel(true)}
            title="Review AI extraction debug information"
          >
            🔍 Debug Review
          </button>
          <div className="debug-button-info">
            <span>Last extraction: {debugInfo.method}</span>
            <span>Confidence: {debugInfo.confidence ? (debugInfo.confidence * 100).toFixed(1) + '%' : 'N/A'}</span>
          </div>
        </div>
      )}
    </>
  );
};

export default DataExtraction; 